import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import { useAIBar } from '../contexts/AIBarContext';
import { useAIInput } from '../hooks/useAIInput';
import { useAIChat } from '../hooks/useAIChat';
import { useAuth } from '../contexts/AuthContext';
import UpgradeNudge from './UpgradeNudge';

// Lazy-loaded so the expanded chat (and its react-markdown dependency) stays
// off the initial bundle — it only renders when the user expands the bar.
const ExpandedAIChat = React.lazy(() => import('./ExpandedAIChat'));

const PersistentAIBar: React.FC = () => {
  const { isExpanded, expand, minimize, messages, addMessage, setLoading, isLoading, upgradeNudge, showUpgradeNudge, hideUpgradeNudge } = useAIBar();
  const { sendMessage, sendVoiceMessage } = useAIChat();
  const {
    isListening,
    hasSpeechRecognition,
    startVoiceRecognition,
    stopVoiceRecognition,
    isPro,
  } = useAIInput();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState('');
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  // Free tier photo/voice allowance (2 lifetime uses each)
  const FREE_PHOTO_LIMIT = 2;
  const FREE_VOICE_LIMIT = 2;
  const canUsePhoto = isPro || ((user as any)?.freePhotoLogsUsed || 0) < FREE_PHOTO_LIMIT;
  const canUseVoice = isPro || ((user as any)?.freeVoiceLogsUsed || 0) < FREE_VOICE_LIMIT;
  const textFieldRef = useRef<HTMLInputElement>(null);

  // Mobile keyboard handling — only respond when an input is focused
  // (not when pinch-zooming, which also changes visualViewport.height)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleVisualViewport = () => {
      if (!window.visualViewport) return;

      // Only adjust for keyboard if the AI bar's own input is focused
      // (not exercise table fields, search inputs, etc.)
      const activeEl = document.activeElement;
      const aiBarEl = document.querySelector('[data-ai-bar-input]');
      const isAIBarFocused = activeEl && aiBarEl && (
        aiBarEl === activeEl || aiBarEl.contains(activeEl)
      );

      if (!isAIBarFocused) {
        setKeyboardOffset(0);
        return;
      }

      const viewportHeight = window.visualViewport.height;
      const windowHeight = window.innerHeight;
      const offset = windowHeight - viewportHeight;

      // Keyboards are typically 200-500px; ignore unreasonable values from zoom
      if (offset > 0 && offset < windowHeight * 0.7) {
        setKeyboardOffset(offset);
      } else {
        setKeyboardOffset(0);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewport);
      window.visualViewport.addEventListener('scroll', handleVisualViewport);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewport);
        window.visualViewport.removeEventListener('scroll', handleVisualViewport);
      }
    };
  }, []);

  // Listen for openAIAssistant events from other components (SmartFoodEntry, FoodLog)
  useEffect(() => {
    const handleOpenAIAssistant = (event: Event) => {
      const customEvent = event as CustomEvent<{ action?: string }>;
      const action = customEvent.detail?.action;

      if (action === 'camera' || action === 'gallery') {
        if (!canUsePhoto) {
          showUpgradeNudge({
            usageType: 'camera',
            title: 'Photo logging is part of Pro',
            message: 'Photograph any meal and every nutrient is logged. Try Pro free for 7 days, or type what you ate.',
          });
          return;
        }
        navigate('/scan', { state: { action } });
      } else if (action === 'voice') {
        // Trigger voice
        handleVoiceClick();
      } else {
        // Just expand
        expand();
      }
    };

    window.addEventListener('openAIAssistant', handleOpenAIAssistant);
    return () => window.removeEventListener('openAIAssistant', handleOpenAIAssistant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, canUsePhoto, canUseVoice, showUpgradeNudge, expand, addMessage, navigate]);

  // Handle sending message
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessageText = inputValue.trim();
    setInputValue('');

    // Add user message to context
    addMessage({
      role: 'user',
      content: userMessageText,
      timestamp: new Date(),
    });

    // Convert messages to format expected by hook
    const chatMessages = messages.map(m => ({
      id: Date.now().toString(),
      text: m.content,
      isUser: m.role === 'user',
      timestamp: m.timestamp,
      type: m.type,
    }));

    setLoading(true);
    // sendMessage adds the assistant reply to the shared context itself —
    // do NOT re-add it here (caused duplicate bubbles)
    await sendMessage(userMessageText, chatMessages);
    setLoading(false);
  };

  // Handle voice input
  const handleVoiceClick = () => {
    if (!canUseVoice) {
      showUpgradeNudge({
        usageType: 'voice',
        title: 'Voice logging is part of Pro',
        message: 'Say what you ate and it is logged. Try Pro free for 7 days, or type it instead.',
      });
      return;
    }

    if (!hasSpeechRecognition) {
      addMessage({
        role: 'assistant',
        content: '⚠️ Speech recognition is not supported in this browser. Try using Chrome or Edge.',
        timestamp: new Date(),
        type: 'general',
      });
      expand();
      return;
    }

    if (isListening) {
      stopVoiceRecognition();
    } else {
      const success = startVoiceRecognition(
        async (transcript) => {
          addMessage({
            role: 'user',
            content: transcript,
            timestamp: new Date(),
          });

          expand(); // Expand to show conversation

          const chatMessages = messages.map(m => ({
            id: Date.now().toString(),
            text: m.content,
            isUser: m.role === 'user',
            timestamp: m.timestamp,
            type: m.type,
          }));

          setLoading(true);
          // sendVoiceMessage adds the assistant reply to the shared context
          // itself — do NOT re-add it here (caused duplicate bubbles)
          await sendVoiceMessage(transcript, chatMessages);
          setLoading(false);
        },
        (errorMessage) => {
          addMessage({
            role: 'assistant',
            content: `⚠️ ${errorMessage}`,
            timestamp: new Date(),
            type: 'general',
          });
          expand();
        },
      );

      if (!success) {
        // Error path already pushed a message via onError above for the
        // common cases; this fallback covers any synchronous rejection.
        return;
      }
    }
  };

  // Camera and gallery clicks navigate to the full-screen scan page
  const showPhotoNudge = () => showUpgradeNudge({
    usageType: 'camera',
    title: 'Photo logging is part of Pro',
    message: 'Photograph any meal and every nutrient is logged. Try Pro free for 7 days, or type what you ate.',
  });

  const handleCameraClick = () => {
    if (!canUsePhoto) { showPhotoNudge(); return; }
    navigate('/scan', { state: { action: 'camera' } });
  };

  const handleGalleryClick = () => {
    if (!canUsePhoto) { showPhotoNudge(); return; }
    navigate('/scan', { state: { action: 'gallery' } });
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle expand button
  const handleExpand = () => {
    expand();
    // Focus text field after expanding
    setTimeout(() => {
      textFieldRef.current?.focus();
    }, 100);
  };

  // Central upgrade-nudge modal — rendered in both collapsed and expanded
  // states so any limit denial (chat 403, photo/voice gate) surfaces here
  const centralUpgradeNudge = (
    <UpgradeNudge
      variant="modal"
      open={!!upgradeNudge}
      onClose={hideUpgradeNudge}
      usageType={upgradeNudge?.usageType}
      message={upgradeNudge?.message}
      title={upgradeNudge?.title}
    />
  );

  if (isExpanded) {
    return (
      <>
        <React.Suspense fallback={null}>
          <ExpandedAIChat />
        </React.Suspense>
        {centralUpgradeNudge}
      </>
    );
  }

  return (
    <>
    <Box
        sx={{
          position: 'fixed',
          bottom: {
            xs: `calc(68px + ${keyboardOffset}px)`, // Above bottom nav (68px) + keyboard offset on mobile
            lg: `${keyboardOffset || 0}px`, // No bottom nav on desktop
          },
          left: { xs: 0, md: '240px' }, // Account for sidebar on desktop
          right: 0,
          zIndex: 1000, // Below bottom nav
          // Instrument panel (2026-11-03): same ground as the tab bar, a cyan hairline above.
          backgroundColor: '#0d121a',
          borderTop: '1px solid rgba(0,224,255,0.18)',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.35)',
          transition: 'bottom 0.2s ease-in-out',
        }}
      >
        <Box
          data-ai-bar-input
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: 1,
            maxWidth: '1200px',
            margin: '0 auto',
          }}
        >
          {/* Expand button */}
          <Tooltip title="Open the chat">
            <IconButton
              onClick={handleExpand}
              size="small"
              sx={{
                color: '#00E0FF',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
          </Tooltip>

          {/* Text input */}
          <TextField
            inputRef={textFieldRef}
            fullWidth
            size="small"
            placeholder="Ask me anything or log food..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={handleExpand} // Auto-expand when typing
            disabled={isLoading}
            InputProps={{
              endAdornment: isLoading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                fontSize: '0.9rem',
              },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,224,255,0.25)' },
            }}
          />

          {/* Voice button */}
          <Tooltip title={canUseVoice ? (isListening ? 'Stop listening' : 'Voice input') : 'Free voice logs used — Go Pro'}>
            <span>
              <IconButton
                onClick={handleVoiceClick}
                disabled={isLoading}
                sx={{
                  color: isListening ? 'error.main' : (canUseVoice ? '#00E0FF' : 'text.disabled'),
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {isListening ? <StopIcon /> : <MicIcon />}
              </IconButton>
            </span>
          </Tooltip>

          {/* Camera button */}
          <Tooltip title={canUsePhoto ? 'Take Photo' : 'Photo logging is part of Pro'}>
            <span>
              <IconButton
                onClick={handleCameraClick}
                disabled={isLoading}
                sx={{ color: canUsePhoto ? '#00E0FF' : 'text.disabled', '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <CameraAltIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Gallery button */}
          <Tooltip title={canUsePhoto ? 'Upload from Gallery' : 'Photo logging is part of Pro'}>
            <span>
              <IconButton
                onClick={handleGalleryClick}
                disabled={isLoading}
                sx={{ color: canUsePhoto ? '#00E0FF' : 'text.disabled', '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <PhotoLibraryIcon />
              </IconButton>
            </span>
          </Tooltip>

        </Box>
      </Box>
    {centralUpgradeNudge}
    </>
  );
};

export default PersistentAIBar;
